import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import DataObjectIcon from '@mui/icons-material/DataObject';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import CodeIcon from '@mui/icons-material/Code';
import DevicesIcon from '@mui/icons-material/Devices';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { toast } from 'react-toastify';
import ColorField from './ColorField';
import HtmlCodeDialog from './HtmlCodeDialog';
import ImageLibraryDialog from './ImageLibraryDialog';
import { useMailEditorConfig } from './mailEditorConfig';
import {
  ELEMENT_TYPE_LABELS,
  ELEMENT_VISIBILITY_OPTIONS,
  isFullHtmlDocument,
  normalizeVisibility,
  sanitizeHtml,
} from './editorUtils';

const VISIBILITY_ICONS = {
  all: DevicesIcon,
  mobile: PhoneIphoneIcon,
  desktop: DesktopWindowsIcon,
};

const SectionLabel = ({ children }) => (
  <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
    {children}
  </Typography>
);

// Uncontrolled number field committing its clamped value on blur / Enter.
const NumberField = ({ label, value, min, max, onCommit, disabled, allowEmpty = false }) => (
  <TextField
    label={label}
    type='number'
    size='small'
    fullWidth
    disabled={disabled}
    defaultValue={value ?? ''}
    inputProps={{ min, max }}
    onKeyDown={event => {
      if (event.key === 'Enter') event.target.blur();
    }}
    onBlur={event => {
      const raw = event.target.value;
      if (raw === '' && allowEmpty) {
        onCommit(undefined);
        return;
      }
      const parsed = Number(raw);
      if (Number.isNaN(parsed)) return;
      const clamped = Math.min(max, Math.max(min, parsed));
      event.target.value = String(clamped);
      onCommit(clamped);
    }}
  />
);

const AlignToggle = ({ value, onChange, disabled }) => (
  <ToggleButtonGroup
    exclusive
    size='small'
    value={value || 'left'}
    disabled={disabled}
    onChange={(event, next) => {
      if (next) onChange(next);
    }}
  >
    <ToggleButton value='left' aria-label='Links'>
      <FormatAlignLeftIcon fontSize='small' />
    </ToggleButton>
    <ToggleButton value='center' aria-label='Zentriert'>
      <FormatAlignCenterIcon fontSize='small' />
    </ToggleButton>
    <ToggleButton value='right' aria-label='Rechts'>
      <FormatAlignRightIcon fontSize='small' />
    </ToggleButton>
  </ToggleButtonGroup>
);

const LabeledSlider = ({ label, value, min, max, onChange, disabled, unit = 'px' }) => (
  <Box>
    <SectionLabel>{`${label}: ${value}${unit ? ` ${unit}` : ''}`}</SectionLabel>
    <Slider
      size='small'
      min={min}
      max={max}
      value={typeof value === 'number' ? value : min}
      disabled={disabled}
      onChange={(event, next) => onChange(next)}
      valueLabelDisplay='auto'
    />
  </Box>
);

// Dialog collecting URL + link text for the rich text "Link einfügen" action.
const InsertLinkDialog = ({ open, onClose, onInsert }) => {
  const urlRef = useRef(null);
  const textRef = useRef(null);

  const handleInsert = () => {
    const url = (urlRef.current?.value || '').trim();
    const text = (textRef.current?.value || '').trim();
    if (!url) {
      toast.error('Bitte eine Ziel-URL angeben.');
      return;
    }
    onInsert(url, text || url);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle>Link einfügen</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField
          label='Ziel-URL'
          size='small'
          fullWidth
          autoFocus
          inputRef={urlRef}
          placeholder='https:// oder {{buchungslink}}'
        />
        <TextField label='Linktext' size='small' fullWidth inputRef={textRef} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant='contained' onClick={handleInsert}>
          Einfügen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Rich-lite text settings: contentEditable HTML box with bold/italic/link,
// variable insertion, font size, color, alignment and padding.
const TextSettings = ({ element, onChange, disabled }) => {
  const editorRef = useRef(null);
  const initialHtmlRef = useRef(sanitizeHtml(element.html));
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [variableAnchor, setVariableAnchor] = useState(null);
  const { variables } = useMailEditorConfig();

  const setEditorNode = node => {
    editorRef.current = node;
    if (node && node.dataset.fzInitialized !== 'true') {
      node.innerHTML = initialHtmlRef.current;
      node.dataset.fzInitialized = 'true';
    }
  };

  const commit = () => {
    if (!editorRef.current) return;
    onChange({ html: sanitizeHtml(editorRef.current.innerHTML) });
  };

  // Focuses the editable box and moves the caret to the end when the current
  // selection is outside of it, so inserts always land inside the text.
  const focusEditor = () => {
    const node = editorRef.current;
    if (!node) return;
    const selection = window.getSelection();
    const inside =
      selection && selection.rangeCount > 0 && node.contains(selection.getRangeAt(0).startContainer);
    node.focus();
    if (!inside) {
      const range = document.createRange();
      range.selectNodeContents(node);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const execFormat = command => {
    if (disabled) return;
    focusEditor();
    document.execCommand(command);
    commit();
  };

  const insertVariable = token => {
    if (disabled) return;
    focusEditor();
    document.execCommand('insertText', false, token);
    commit();
  };

  const insertLink = (url, text) => {
    if (disabled) return;
    focusEditor();
    const html = `<a href="${url.replace(/"/g, '&quot;')}" target="_blank" rel="noopener">${text}</a>`;
    document.execCommand('insertHTML', false, sanitizeHtml(html));
    commit();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <SectionLabel>Textinhalt</SectionLabel>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
          <Tooltip title='Fett'>
            <IconButton size='small' disabled={disabled} onClick={() => execFormat('bold')}>
              <FormatBoldIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Kursiv'>
            <IconButton size='small' disabled={disabled} onClick={() => execFormat('italic')}>
              <FormatItalicIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          <Tooltip title='Link einfügen'>
            <IconButton size='small' disabled={disabled} onClick={() => setLinkDialogOpen(true)}>
              <InsertLinkIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          {variables.length > 0 && (
            <>
              <Button
                size='small'
                startIcon={<DataObjectIcon />}
                disabled={disabled}
                onClick={event => setVariableAnchor(event.currentTarget)}
              >
                Variable einfügen
              </Button>
              <Menu anchorEl={variableAnchor} open={Boolean(variableAnchor)} onClose={() => setVariableAnchor(null)}>
                {variables.map(variable => (
                  <MenuItem
                    key={variable.token}
                    onClick={() => {
                      setVariableAnchor(null);
                      insertVariable(variable.token);
                    }}
                  >
                    {variable.label ? `${variable.label} – ${variable.token}` : variable.token}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
        <Box
          ref={setEditorNode}
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={commit}
          onBlur={commit}
          sx={{
            minHeight: 120,
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            fontSize: 14,
            outline: 'none',
            '&:focus': { borderColor: 'primary.main' },
            '& p': { m: 0 },
          }}
        />
      </Box>
      <NumberField
        label='Schriftgröße (px)'
        value={element.fontSize}
        min={8}
        max={72}
        allowEmpty
        disabled={disabled}
        onCommit={fontSize => onChange({ fontSize })}
      />
      <ColorField
        label='Textfarbe'
        value={element.color || '#333333'}
        disabled={disabled}
        onChange={color => onChange({ color })}
      />
      <Box>
        <SectionLabel>Ausrichtung</SectionLabel>
        <AlignToggle value={element.align} disabled={disabled} onChange={align => onChange({ align })} />
      </Box>
      <LabeledSlider
        label='Innenabstand'
        value={element.padding ?? 12}
        min={0}
        max={48}
        disabled={disabled}
        onChange={padding => onChange({ padding })}
      />
      <InsertLinkDialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} onInsert={insertLink} />
    </Box>
  );
};

// Image settings: pick or upload via the shared image library, alt text, width,
// alignment, link and padding.
const ImageSettings = ({ element, onChange, disabled }) => {
  const { listAssets } = useMailEditorConfig();
  const [libraryOpen, setLibraryOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {element.src && (
        <Box
          component='img'
          src={element.src}
          alt={element.alt || ''}
          sx={{ maxWidth: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
        />
      )}
      <Button
        variant='outlined'
        startIcon={<PhotoLibraryIcon />}
        disabled={disabled || !listAssets}
        onClick={() => setLibraryOpen(true)}
      >
        {element.src ? 'Bild austauschen' : 'Bild aus der Ablage wählen'}
      </Button>
      <ImageLibraryDialog
        open={libraryOpen}
        selectedUrl={element.src}
        onClose={() => setLibraryOpen(false)}
        onSelect={asset => onChange({ src: asset.url })}
      />
      <TextField
        label='Alt-Text'
        size='small'
        fullWidth
        disabled={disabled}
        defaultValue={element.alt || ''}
        onBlur={event => onChange({ alt: event.target.value })}
      />
      <LabeledSlider
        label='Breite'
        value={element.widthPercent ?? 100}
        min={10}
        max={100}
        unit='%'
        disabled={disabled}
        onChange={widthPercent => onChange({ widthPercent })}
      />
      <Box>
        <SectionLabel>Ausrichtung</SectionLabel>
        <AlignToggle value={element.align || 'center'} disabled={disabled} onChange={align => onChange({ align })} />
      </Box>
      <TextField
        label='Link auf Bild'
        size='small'
        fullWidth
        disabled={disabled}
        defaultValue={element.link || ''}
        placeholder='https://'
        onBlur={event => onChange({ link: event.target.value.trim() })}
      />
      <LabeledSlider
        label='Innenabstand'
        value={element.padding ?? 12}
        min={0}
        max={48}
        disabled={disabled}
        onChange={padding => onChange({ padding })}
      />
    </Box>
  );
};

// Button settings: label, link with variable quick-insert, colors, alignment,
// corner radius and paddings.
const ButtonSettings = ({ element, onChange, disabled }) => {
  const linkRef = useRef(null);
  const { variables } = useMailEditorConfig();

  const appendVariableToLink = token => {
    if (!linkRef.current) return;
    const next = `${linkRef.current.value || ''}${token}`;
    linkRef.current.value = next;
    onChange({ link: next });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label='Button-Text'
        size='small'
        fullWidth
        disabled={disabled}
        defaultValue={element.label || ''}
        onBlur={event => onChange({ label: event.target.value })}
      />
      <Box>
        <TextField
          label='Ziel-Link'
          size='small'
          fullWidth
          disabled={disabled}
          inputRef={linkRef}
          defaultValue={element.link || ''}
          placeholder='https:// oder {{buchungslink}}'
          helperText={
            variables.length > 0
              ? 'Variablen als Linkziel sind erlaubt.'
              : 'Absolute URL, z. B. https://fun-zone.de'
          }
          onBlur={event => onChange({ link: event.target.value.trim() })}
        />
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
          {variables.map(variable => (
            <Chip
              key={variable.token}
              label={variable.token}
              size='small'
              variant='outlined'
              disabled={disabled}
              onClick={() => appendVariableToLink(variable.token)}
            />
          ))}
        </Box>
      </Box>
      <ColorField
        label='Button-Farbe'
        value={element.backgroundColor || '#00228f'}
        disabled={disabled}
        onChange={backgroundColor => onChange({ backgroundColor })}
      />
      <ColorField
        label='Textfarbe'
        value={element.textColor || '#ffffff'}
        disabled={disabled}
        onChange={textColor => onChange({ textColor })}
      />
      <Box>
        <SectionLabel>Ausrichtung</SectionLabel>
        <AlignToggle value={element.align || 'center'} disabled={disabled} onChange={align => onChange({ align })} />
      </Box>
      <LabeledSlider
        label='Rundung'
        value={element.borderRadius ?? 6}
        min={0}
        max={24}
        disabled={disabled}
        onChange={borderRadius => onChange({ borderRadius })}
      />
      <NumberField
        label='Schriftgröße (px)'
        value={element.fontSize ?? 16}
        min={8}
        max={48}
        disabled={disabled}
        onCommit={fontSize => onChange({ fontSize })}
      />
      <LabeledSlider
        label='Innenabstand (Button)'
        value={element.innerPadding ?? 12}
        min={4}
        max={32}
        disabled={disabled}
        onChange={innerPadding => onChange({ innerPadding })}
      />
      <LabeledSlider
        label='Innenabstand (außen)'
        value={element.padding ?? 12}
        min={0}
        max={48}
        disabled={disabled}
        onChange={padding => onChange({ padding })}
      />
    </Box>
  );
};

const SpacerSettings = ({ element, onChange, disabled }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <LabeledSlider
      label='Höhe'
      value={element.height ?? 20}
      min={4}
      max={200}
      disabled={disabled}
      onChange={height => onChange({ height })}
    />
    <NumberField
      label='Höhe (px)'
      value={element.height ?? 20}
      min={4}
      max={200}
      disabled={disabled}
      onCommit={height => onChange({ height })}
    />
  </Box>
);

const DividerSettings = ({ element, onChange, disabled }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <ColorField
      label='Farbe'
      value={element.color || '#dddddd'}
      disabled={disabled}
      onChange={color => onChange({ color })}
    />
    <LabeledSlider
      label='Dicke'
      value={element.thickness ?? 1}
      min={1}
      max={8}
      disabled={disabled}
      onChange={thickness => onChange({ thickness })}
    />
    <LabeledSlider
      label='Breite'
      value={element.widthPercent ?? 100}
      min={10}
      max={100}
      unit='%'
      disabled={disabled}
      onChange={widthPercent => onChange({ widthPercent })}
    />
    <NumberField
      label='Abstand oben (px)'
      value={element.paddingTop ?? 12}
      min={0}
      max={80}
      disabled={disabled}
      onCommit={paddingTop => onChange({ paddingTop })}
    />
    <NumberField
      label='Abstand unten (px)'
      value={element.paddingBottom ?? 12}
      min={0}
      max={80}
      disabled={disabled}
      onCommit={paddingBottom => onChange({ paddingBottom })}
    />
  </Box>
);

// HTML settings: opens the code dialog, shows what kind of markup is stored and
// keeps the outer padding adjustable like on every other element.
const HtmlSettings = ({ element, onChange, disabled }) => {
  const [codeDialogOpen, setCodeDialogOpen] = useState(false);
  const html = String(element.html || '');
  const lineCount = html ? html.split('\n').length : 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='body2' color='text.secondary'>
        Eigenes HTML als Baustein — beliebig mit den anderen Grundelementen kombinierbar.
      </Typography>
      <Button
        variant='contained'
        startIcon={<CodeIcon />}
        disabled={disabled}
        onClick={() => setCodeDialogOpen(true)}
      >
        {html ? 'HTML bearbeiten' : 'HTML hinzufügen'}
      </Button>
      {html ? (
        <Typography variant='caption' color='text.secondary'>
          {`${lineCount} ${lineCount === 1 ? 'Zeile' : 'Zeilen'}, ${html.length} Zeichen`}
        </Typography>
      ) : (
        <Typography variant='caption' color='text.secondary'>
          Noch kein HTML hinterlegt.
        </Typography>
      )}
      {isFullHtmlDocument(html) && (
        <Typography variant='caption' color='text.secondary'>
          Vollständiges HTML-Dokument erkannt: Stile und Body-Inhalt werden übernommen, der
          Dokumentrahmen entfällt, sobald weitere Elemente in der Mail stehen.
        </Typography>
      )}
      <LabeledSlider
        label='Innenabstand'
        value={element.padding ?? 0}
        min={0}
        max={48}
        disabled={disabled}
        onChange={padding => onChange({ padding })}
      />
      <HtmlCodeDialog
        open={codeDialogOpen}
        value={html}
        disabled={disabled}
        onClose={() => setCodeDialogOpen(false)}
        onConfirm={value => onChange({ html: value })}
      />
    </Box>
  );
};

// Device visibility of a single element. Applies to every element type: an
// element can be duplicated so that one copy runs on mobile and the other on
// desktop.
const VisibilitySettings = ({ element, onChange, disabled }) => {
  const value = normalizeVisibility(element.visibility);

  return (
    <Box>
      <SectionLabel>Sichtbarkeit</SectionLabel>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size='small'
        value={value}
        disabled={disabled}
        onChange={(event, next) => {
          if (next) onChange({ visibility: next });
        }}
      >
        {ELEMENT_VISIBILITY_OPTIONS.map(option => {
          const Icon = VISIBILITY_ICONS[option.value];
          return (
            <ToggleButton
              key={option.value}
              value={option.value}
              sx={{ flexDirection: 'column', gap: 0.25, py: 0.75, fontSize: 10, lineHeight: 1.2 }}
            >
              <Icon fontSize='small' />
              {option.label}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
      {value !== 'all' && (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 0.5 }}>
          Duplizieren Sie das Element, um für die andere Geräteklasse eine eigene Variante zu bauen.
        </Typography>
      )}
    </Box>
  );
};

// Settings for the mandatory footer block (Pflichtbereich).
const FooterSettings = ({ footer, onChange, disabled }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Typography variant='body2' color='text.secondary'>
      Der Fußbereich mit Abmeldelink ist Pflicht und wird automatisch an jede E-Mail angehängt.
    </Typography>
    <TextField
      label='Fußzeilentext'
      size='small'
      fullWidth
      multiline
      minRows={4}
      disabled={disabled}
      defaultValue={footer.text || ''}
      onBlur={event => onChange({ text: event.target.value })}
    />
    <TextField
      label='Beschriftung Abmeldelink'
      size='small'
      fullWidth
      disabled={disabled}
      defaultValue={footer.unsubscribeLabel || ''}
      placeholder='Abmelden'
      onBlur={event => onChange({ unsubscribeLabel: event.target.value })}
    />
  </Box>
);

// "Eigenschaften" sidebar panel for the selected element or the footer block.
// Replaces the tab content and returns to the tabs via the back button.
const ElementSettingsPanel = ({ element, footer, onElementChange, onFooterChange, onBack, disabled }) => {
  const isFooter = !element;
  const title = isFooter ? 'Fußbereich' : ELEMENT_TYPE_LABELS[element.type] || 'Element';

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton size='small' onClick={onBack} aria-label='Zurück zu den Elementen'>
          <ArrowBackIcon fontSize='small' />
        </IconButton>
        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
          {`Eigenschaften – ${title}`}
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {!isFooter && (
        <>
          <VisibilitySettings element={element} onChange={onElementChange} disabled={disabled} />
          <Divider sx={{ my: 2 }} />
        </>
      )}
      {isFooter && <FooterSettings footer={footer} onChange={onFooterChange} disabled={disabled} />}
      {!isFooter && element.type === 'text' && (
        <TextSettings element={element} onChange={onElementChange} disabled={disabled} />
      )}
      {!isFooter && element.type === 'image' && (
        <ImageSettings element={element} onChange={onElementChange} disabled={disabled} />
      )}
      {!isFooter && element.type === 'button' && (
        <ButtonSettings element={element} onChange={onElementChange} disabled={disabled} />
      )}
      {!isFooter && element.type === 'spacer' && (
        <SpacerSettings element={element} onChange={onElementChange} disabled={disabled} />
      )}
      {!isFooter && element.type === 'divider' && (
        <DividerSettings element={element} onChange={onElementChange} disabled={disabled} />
      )}
      {!isFooter && element.type === 'html' && (
        <HtmlSettings element={element} onChange={onElementChange} disabled={disabled} />
      )}
    </Box>
  );
};

export default ElementSettingsPanel;
